import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { carName } from '../../domain/cars/carCatalogue';
import { formatSpecNumber } from '../../domain/cars/carProperties';

/**
 * What the race said (SPEC-008 PR-8.5).
 *
 * The verdict, the margin in both of its units, and — the part this band exists
 * for — WHAT THESE CONDITIONS COST EACH CAR against the figure its maker
 * published. A snow time on its own is a number nobody has a reference for; the
 * same number beside the car's own dry 0–100 is the whole argument of the
 * screen in one line.
 *
 * The margin is printed twice because it is two different facts (D-13). Seconds
 * is what a stopwatch gives and what a reader would quote; metres is where the
 * loser actually was when the winner crossed, which is the gap the strip just
 * drew. On dry tarmac they are a tenth and a few metres; on snow they are four
 * seconds and eighty metres, and a reader who saw only the first would not
 * believe the second.
 */

/** How much slower than its published figure is worth remarking on at all. */
const NOTABLE_LOSS_SECONDS = 0.05;

function LaneVerdict({ car, distance, lane }) {
    const t = useTranslate();
    const { languageCode } = useLocale();
    const seconds = (value, digits = 2) => formatSpecNumber(value, { languageCode, fractionDigits: digits });
    const lost = lane.zeroToHundred === null ? null : lane.zeroToHundred - lane.publishedZeroToHundred;

    return (
        <div className="race-result__lane">
            <p className="race-result__car">{carName(car)}</p>

            <dl className="race-result__figures">
                <div className="race-result__figure">
                    <dt>{t('compare.race.elapsed', { metres: formatSpecNumber(distance, { languageCode }) })}</dt>
                    <dd>
                        {lane.finished
                            ? t('compare.race.seconds', { seconds: seconds(lane.seconds) })
                            : t('compare.race.noFinish')}
                    </dd>
                </div>

                <div className="race-result__figure">
                    <dt>{t('compare.race.trap')}</dt>
                    <dd>
                        {t('compare.race.speed', {
                            speed: formatSpecNumber(lane.trapKmh, { languageCode }),
                        })}
                    </dd>
                </div>

                <div className="race-result__figure">
                    <dt>{t('cars.spec.zeroToHundred')}</dt>
                    {/* The three cases are genuinely different claims and they
                        are said in three different sentences rather than in one
                        with a signed number in it: the car matched its published
                        figure, it lost time to the road, or it never reached
                        100 km/h at all — which on a snow climb is the answer. */}
                    <dd>
                        {lost === null
                            ? t('compare.race.neverReached')
                            : t(lost > NOTABLE_LOSS_SECONDS ? 'compare.race.zeroToHundredLost' : 'compare.race.zeroToHundredHeld', {
                                seconds: seconds(lane.zeroToHundred, 1),
                                lost: seconds(Math.abs(lost), 1),
                                published: seconds(lane.publishedZeroToHundred, 1),
                            })}
                    </dd>
                </div>
            </dl>
        </div>
    );
}

export default function DragRaceResult({ cars, race }) {
    const t = useTranslate();
    const { languageCode } = useLocale();
    const winnerIndex = race.lanes.findIndex((lane) => lane.carId === race.winner);
    const winner = winnerIndex >= 0 ? cars[winnerIndex] : null;
    const loserLane = winnerIndex >= 0 ? race.lanes[1 - winnerIndex] : null;

    return (
        <section className="race-result" aria-live="polite">
            <p className="race-result__verdict">
                {(() => {
                    if (!winner) {
                        return t('compare.race.deadHeat');
                    }

                    // A race nobody finished is decided on distance, so the
                    // sentence has to be about distance — "by 0.00 seconds"
                    // under two cars that never crossed the line would be the
                    // screen contradicting what the reader just watched.
                    if (!loserLane.finished) {
                        return t('compare.race.wonUnfinished', {
                            winner: carName(winner),
                            metres: formatSpecNumber(race.marginMetres, { languageCode }),
                        });
                    }

                    return t('compare.race.won', {
                        winner: carName(winner),
                        seconds: formatSpecNumber(race.marginSeconds, { languageCode, fractionDigits: 2 }),
                        metres: formatSpecNumber(race.marginMetres, { languageCode, fractionDigits: 1 }),
                    });
                })()}
            </p>

            <div className="race-result__lanes">
                {race.lanes.map((lane, index) => (
                    <LaneVerdict
                        key={`${lane.carId}-${index}`}
                        car={cars[index]}
                        distance={race.distance}
                        lane={lane}
                    />
                ))}
            </div>

            {/* Printed rather than tucked away, and it is the line that makes
                every figure above it citable rather than merely plausible: this
                is a model, it is anchored to the two figures the catalogue
                publishes, and everything else it says is a consequence of
                physics rather than a measurement anybody took. */}
            <p className="race-result__basis">{t('compare.race.basis')}</p>
        </section>
    );
}
